import React, { useEffect, useRef, useState } from 'react';

/**
 * React 19 compatible ScrollAnimation component
 * Drop-in replacement for react-animate-on-scroll
 */
const ScrollAnimation = ({
  children,
  animateIn = 'fadeIn',
  animateOut = 'fadeOut',
  animateOnce = false,
  offset = 150,
  duration = 1,
  delay = 0,
  initiallyVisible = false,
  style = {},
  scrollableParentSelector = null,
  afterAnimatedIn = null,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(initiallyVisible);
  const [hasAnimated, setHasAnimated] = useState(initiallyVisible);
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Create Intersection Observer
    const observerOptions = {
      root: scrollableParentSelector 
        ? document.querySelector(scrollableParentSelector) 
        : null,
      rootMargin: `${offset}px`,
      threshold: 0.01,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!hasAnimated) {
            setIsVisible(true);
            setHasAnimated(true);
            if (afterAnimatedIn) {
              // Call callback after animation duration
              setTimeout(() => {
                afterAnimatedIn();
              }, (duration * 1000) + delay);
            }
          }
        } else if (!animateOnce) {
          setIsVisible(false);
          setHasAnimated(false);
        }
      });
    }, observerOptions);

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [offset, animateOnce, scrollableParentSelector, duration, delay, hasAnimated, afterAnimatedIn]);

  // Determine animation class
  let animationClass = '';
  if (isVisible) {
    animationClass = animateIn;
  } else if (!animateOnce && hasAnimated) {
    animationClass = animateOut;
  }
  
  // Build style object
  const elementStyle = {
    ...style,
    animationDuration: `${duration}s`,
    animationDelay: `${delay}ms`,
  };

  return (
    <div
      ref={elementRef}
      className={animationClass}
      style={elementStyle}
      {...props}
    >
      {children}
    </div>
  );
};

export default ScrollAnimation;
